/**
 * Deterministic mock seeds for B1.9 InvoiceList.
 *
 * 12 satırlık realistik bir şube günlüğü — farklı müşteri tipleri,
 * statüleri, tender kombinasyonları, e-Arşiv vs e-Fatura ayrımı.
 * B1.7 Cashier + B1.8 Payment + B1.10 Day Close seed'leri ile
 * hikâye olarak uyumlu (aynı şube, aynı gün).
 */

import type {
  InvoiceCustomerSnapshot,
  InvoiceLine,
  InvoicePaymentRef,
  InvoiceRow,
} from './types';

const BRANCH_ID = 'branch-patiland-kadikoy';
const BRANCH_NAME = 'PatiLand Kadıköy';
const TODAY = '2026-04-14';

function iso(hh: number, mm: number): string {
  const h = String(hh).padStart(2, '0');
  const m = String(mm).padStart(2, '0');
  return `${TODAY}T${h}:${m}:00+03:00`;
}

const CUSTOMERS: readonly InvoiceCustomerSnapshot[] = [
  {
    id: 'cus-001',
    name: 'Zeynep Aksoy',
    email: 'zeynep.aksoy@example.com',
    phone: '+90 532 111 22 33',
    taxNumber: '12345678901',
    taxOffice: null,
    type: 'individual',
    isEfaturaRegistered: false,
  },
  {
    id: 'cus-002',
    name: 'Mehmet Demir',
    email: 'mehmet.demir@example.com',
    phone: '+90 533 444 55 66',
    taxNumber: null,
    taxOffice: null,
    type: 'individual',
    isEfaturaRegistered: false,
  },
  {
    id: 'cus-003',
    name: 'Ayşe Yıldız',
    email: null,
    phone: '+90 535 777 88 99',
    taxNumber: null,
    taxOffice: null,
    type: 'individual',
    isEfaturaRegistered: false,
  },
  {
    id: 'cus-004',
    name: 'Petshop Milano Ltd. Şti.',
    email: 'muhasebe@petshopmilano.com.tr',
    phone: '+90 212 555 00 11',
    taxNumber: '1234567890',
    taxOffice: 'Beşiktaş',
    type: 'business',
    isEfaturaRegistered: true,
  },
  {
    id: 'cus-005',
    name: 'Kan Durusan',
    email: 'kan@example.com',
    phone: '+90 544 222 33 44',
    taxNumber: null,
    taxOffice: null,
    type: 'individual',
    isEfaturaRegistered: false,
  },
  {
    id: 'cus-006',
    name: 'PetMarket AŞ',
    email: 'finans@petmarket.com.tr',
    phone: '+90 216 333 44 55',
    taxNumber: '9876543210',
    taxOffice: 'Kadıköy',
    type: 'business',
    isEfaturaRegistered: true,
  },
];

function line(
  id: string,
  description: string,
  quantity: number,
  unitMinor: number,
  discountMinor = 0,
  taxRate = 0.2,
  sku: string | null = null,
): InvoiceLine {
  const subtotalMinor = unitMinor * quantity - discountMinor;
  const taxMinor = Math.round(subtotalMinor * taxRate);
  const totalMinor = subtotalMinor + taxMinor;
  return {
    id,
    sku,
    description,
    quantity,
    unitPriceMinor: unitMinor,
    taxRate,
    taxMinor,
    discountMinor,
    subtotalMinor,
    totalMinor,
  };
}

function sumLines(lines: readonly InvoiceLine[]): {
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;
} {
  return lines.reduce(
    (acc, l) => ({
      subtotalMinor: acc.subtotalMinor + l.subtotalMinor,
      discountMinor: acc.discountMinor + l.discountMinor,
      taxMinor: acc.taxMinor + l.taxMinor,
      totalMinor: acc.totalMinor + l.totalMinor,
    }),
    { subtotalMinor: 0, discountMinor: 0, taxMinor: 0, totalMinor: 0 },
  );
}

function cashPayment(
  amountMinor: number,
  paidAt: string,
  transactionId: string,
): InvoicePaymentRef {
  return { method: 'cash', amountMinor, paidAt, transactionId };
}

function cardPayment(
  amountMinor: number,
  paidAt: string,
  transactionId: string,
): InvoicePaymentRef {
  return { method: 'card', amountMinor, paidAt, transactionId };
}

export const SEED_INVOICE_ROWS: readonly InvoiceRow[] = [
  // #1 — paid, e-arsiv, nakit
  (() => {
    const lines = [
      line('ln-001-1', 'Tam Bakım Paketi (küçük ırk)', 1, 65000, 0, 0.2, 'SVC-FULL-S'),
    ];
    const totals = sumLines(lines);
    const payments = [cashPayment(totals.totalMinor, iso(9, 45), 'txn-0001')];
    return {
      id: 'inv-001',
      invoiceNumber: 'EA20260414000001',
      type: 'e_arsiv',
      status: 'paid',
      customer: CUSTOMERS[0],
      issuedAt: iso(9, 30),
      dueAt: null,
      ...totals,
      paidMinor: totals.totalMinor,
      balanceMinor: 0,
      linkedOrderId: 'order-001',
      linkedBookingId: 'bkg-9821',
      lines,
      payments,
      deliveryStatus: 'opened',
      pdfUrl: '/mock-invoices/inv-001.pdf',
      shareUrl: 'https://pay.petota.biz/inv/001',
      gibCode: 'A1B2C3D4-E5F6-7890-1234-567890ABCDEF',
      integrator: 'parasut',
      integratorError: null,
      notes: null,
      createdAt: iso(9, 28),
      updatedAt: iso(9, 45),
    } satisfies InvoiceRow;
  })(),

  // #2 — paid, e-arsiv, kart (split 2 hizmet)
  (() => {
    const lines = [
      line('ln-002-1', 'Tırnak Kesim', 1, 8000, 0, 0.2),
      line('ln-002-2', 'Diş Fırçalama', 1, 12000, 1000, 0.2),
    ];
    const totals = sumLines(lines);
    const payments = [cardPayment(totals.totalMinor, iso(10, 20), 'txn-0002')];
    return {
      id: 'inv-002',
      invoiceNumber: 'EA20260414000002',
      type: 'e_arsiv',
      status: 'paid',
      customer: CUSTOMERS[1],
      issuedAt: iso(10, 15),
      dueAt: null,
      ...totals,
      paidMinor: totals.totalMinor,
      balanceMinor: 0,
      linkedOrderId: 'order-002',
      linkedBookingId: 'bkg-9822',
      lines,
      payments,
      deliveryStatus: 'sent_email',
      pdfUrl: '/mock-invoices/inv-002.pdf',
      shareUrl: 'https://pay.petota.biz/inv/002',
      gibCode: 'B1C2D3E4-F5G6-7890-1234-567890ABCDEF',
      integrator: 'parasut',
      integratorError: null,
      notes: null,
      createdAt: iso(10, 12),
      updatedAt: iso(10, 22),
    } satisfies InvoiceRow;
  })(),

  // #3 — issued (not yet paid — on_account)
  (() => {
    const lines = [
      line('ln-003-1', 'Pet Taksi Randevusu (şehir içi)', 1, 15000, 0, 0.2),
      line('ln-003-2', 'Banyo + Saç Kesimi', 1, 45000, 0, 0.2),
    ];
    const totals = sumLines(lines);
    return {
      id: 'inv-003',
      invoiceNumber: 'EA20260414000003',
      type: 'e_arsiv',
      status: 'issued',
      customer: CUSTOMERS[2],
      issuedAt: iso(11, 0),
      dueAt: '2026-04-28',
      ...totals,
      paidMinor: 0,
      balanceMinor: totals.totalMinor,
      linkedOrderId: 'order-003',
      linkedBookingId: 'bkg-9823',
      lines,
      payments: [],
      deliveryStatus: 'not_sent',
      pdfUrl: '/mock-invoices/inv-003.pdf',
      shareUrl: 'https://pay.petota.biz/inv/003',
      gibCode: 'C1D2E3F4-G5H6-7890-1234-567890ABCDEF',
      integrator: 'parasut',
      integratorError: null,
      notes: 'Müşteri SMS ile hatırlatma istedi',
      createdAt: iso(10, 58),
      updatedAt: iso(11, 0),
    } satisfies InvoiceRow;
  })(),

  // #4 — e-Fatura (B2B), paid, havale
  (() => {
    const lines = [
      line('ln-004-1', 'Toplu pet mama yem (25kg × 40)', 40, 120000, 50000, 0.2, 'SKU-FOOD-25'),
      line('ln-004-2', 'Teslimat hizmeti', 1, 25000, 0, 0.2),
    ];
    const totals = sumLines(lines);
    const payments: readonly InvoicePaymentRef[] = [
      { method: 'transfer', amountMinor: totals.totalMinor, paidAt: iso(12, 0), transactionId: 'txn-0004' },
    ];
    return {
      id: 'inv-004',
      invoiceNumber: 'EF20260414000001',
      type: 'e_fatura',
      status: 'paid',
      customer: CUSTOMERS[3],
      issuedAt: iso(11, 45),
      dueAt: '2026-04-21',
      ...totals,
      paidMinor: totals.totalMinor,
      balanceMinor: 0,
      linkedOrderId: 'order-004',
      linkedBookingId: null,
      lines,
      payments,
      deliveryStatus: 'delivered',
      pdfUrl: '/mock-invoices/inv-004.pdf',
      shareUrl: null,
      gibCode: 'ETTN-2026-04-14-4A7B2C',
      integrator: 'logo',
      integratorError: null,
      notes: null,
      createdAt: iso(11, 40),
      updatedAt: iso(12, 0),
    } satisfies InvoiceRow;
  })(),

  // #5 — draft (cashier'da POS açık, henüz issue edilmedi)
  (() => {
    const lines = [
      line('ln-005-1', 'Kedi Bakım Mini Paketi', 1, 28000, 0, 0.2),
    ];
    const totals = sumLines(lines);
    return {
      id: 'inv-005',
      invoiceNumber: null,
      type: 'e_arsiv',
      status: 'draft',
      customer: CUSTOMERS[4],
      issuedAt: null,
      dueAt: null,
      ...totals,
      paidMinor: 0,
      balanceMinor: totals.totalMinor,
      linkedOrderId: 'order-005',
      linkedBookingId: 'bkg-9825',
      lines,
      payments: [],
      deliveryStatus: 'not_sent',
      pdfUrl: null,
      shareUrl: null,
      gibCode: null,
      integrator: null,
      integratorError: null,
      notes: null,
      createdAt: iso(12, 30),
      updatedAt: iso(12, 30),
    } satisfies InvoiceRow;
  })(),

  // #6 — failed (integrator hata, retry bekliyor)
  (() => {
    const lines = [
      line('ln-006-1', 'Acil veteriner muayene', 1, 55000, 0, 0.2),
      line('ln-006-2', 'Kan tahlili', 1, 22000, 0, 0.2),
    ];
    const totals = sumLines(lines);
    return {
      id: 'inv-006',
      invoiceNumber: null,
      type: 'e_arsiv',
      status: 'failed',
      customer: CUSTOMERS[1],
      issuedAt: null,
      dueAt: null,
      ...totals,
      paidMinor: 0,
      balanceMinor: totals.totalMinor,
      linkedOrderId: 'order-006',
      linkedBookingId: 'bkg-9826',
      lines,
      payments: [],
      deliveryStatus: 'not_sent',
      pdfUrl: null,
      shareUrl: null,
      gibCode: null,
      integrator: 'parasut',
      integratorError: 'GIB_SYSTEM_UNAVAILABLE (HTTP 503) — 17:42\'de retry',
      notes: null,
      createdAt: iso(13, 15),
      updatedAt: iso(13, 16),
    } satisfies InvoiceRow;
  })(),

  // #7 — voided (iptal, düzeltme faturası kesiliyor)
  (() => {
    const lines = [
      line('ln-007-1', 'Diş Taşı Temizliği', 1, 85000, 0, 0.2),
    ];
    const totals = sumLines(lines);
    return {
      id: 'inv-007',
      invoiceNumber: 'EA20260414000007',
      type: 'e_arsiv',
      status: 'voided',
      customer: CUSTOMERS[0],
      issuedAt: iso(13, 30),
      dueAt: null,
      ...totals,
      paidMinor: 0,
      balanceMinor: 0,
      linkedOrderId: 'order-007',
      linkedBookingId: 'bkg-9827',
      lines,
      payments: [],
      deliveryStatus: 'sent_email',
      pdfUrl: '/mock-invoices/inv-007.pdf',
      shareUrl: null,
      gibCode: 'D1E2F3G4-H5I6-7890-1234-567890ABCDEF',
      integrator: 'parasut',
      integratorError: null,
      notes: 'Yanlış hizmet kesildi, düzeltme faturası: inv-008',
      createdAt: iso(13, 25),
      updatedAt: iso(13, 55),
    } satisfies InvoiceRow;
  })(),

  // #8 — düzeltme (yukarıdaki iptal için)
  (() => {
    const lines = [
      line('ln-008-1', 'Periodontal Tedavi', 1, 95000, 0, 0.2),
    ];
    const totals = sumLines(lines);
    const payments = [cardPayment(totals.totalMinor, iso(14, 10), 'txn-0008')];
    return {
      id: 'inv-008',
      invoiceNumber: 'EA20260414000008',
      type: 'e_arsiv',
      status: 'paid',
      customer: CUSTOMERS[0],
      issuedAt: iso(14, 0),
      dueAt: null,
      ...totals,
      paidMinor: totals.totalMinor,
      balanceMinor: 0,
      linkedOrderId: 'order-008',
      linkedBookingId: 'bkg-9827',
      lines,
      payments,
      deliveryStatus: 'opened',
      pdfUrl: '/mock-invoices/inv-008.pdf',
      shareUrl: 'https://pay.petota.biz/inv/008',
      gibCode: 'E1F2G3H4-I5J6-7890-1234-567890ABCDEF',
      integrator: 'parasut',
      integratorError: null,
      notes: 'inv-007 iptali yerine kesildi',
      createdAt: iso(13, 58),
      updatedAt: iso(14, 10),
    } satisfies InvoiceRow;
  })(),

  // #9 — e-Fatura vadeli, issued, henüz ödenmedi
  (() => {
    const lines = [
      line('ln-009-1', 'Ay sonu toplu bakım sözleşmesi', 1, 450000, 25000, 0.2),
    ];
    const totals = sumLines(lines);
    return {
      id: 'inv-009',
      invoiceNumber: 'EF20260414000002',
      type: 'e_fatura',
      status: 'issued',
      customer: CUSTOMERS[5],
      issuedAt: iso(14, 30),
      dueAt: '2026-05-14',
      ...totals,
      paidMinor: 0,
      balanceMinor: totals.totalMinor,
      linkedOrderId: null,
      linkedBookingId: null,
      lines,
      payments: [],
      deliveryStatus: 'delivered',
      pdfUrl: '/mock-invoices/inv-009.pdf',
      shareUrl: null,
      gibCode: 'ETTN-2026-04-14-9F3C1A',
      integrator: 'logo',
      integratorError: null,
      notes: 'Vade: 30 gün',
      createdAt: iso(14, 25),
      updatedAt: iso(14, 32),
    } satisfies InvoiceRow;
  })(),

  // #10 — paid, kısmi ödeme + peşinat kapatıldı
  (() => {
    const lines = [
      line('ln-010-1', 'Haftalık pansiyon (7 gün)', 7, 30000, 0, 0.2),
    ];
    const totals = sumLines(lines);
    const payments: readonly InvoicePaymentRef[] = [
      cardPayment(Math.round(totals.totalMinor * 0.5), iso(8, 30), 'txn-0010a'),
      cardPayment(Math.round(totals.totalMinor * 0.5), iso(15, 0), 'txn-0010b'),
    ];
    return {
      id: 'inv-010',
      invoiceNumber: 'EA20260414000010',
      type: 'e_arsiv',
      status: 'paid',
      customer: CUSTOMERS[2],
      issuedAt: iso(15, 5),
      dueAt: null,
      ...totals,
      paidMinor: totals.totalMinor,
      balanceMinor: 0,
      linkedOrderId: 'order-010',
      linkedBookingId: 'bkg-9830',
      lines,
      payments,
      deliveryStatus: 'sent_whatsapp',
      pdfUrl: '/mock-invoices/inv-010.pdf',
      shareUrl: 'https://pay.petota.biz/inv/010',
      gibCode: 'F1G2H3I4-J5K6-7890-1234-567890ABCDEF',
      integrator: 'parasut',
      integratorError: null,
      notes: null,
      createdAt: iso(8, 25),
      updatedAt: iso(15, 5),
    } satisfies InvoiceRow;
  })(),

  // #11 — dün kesilmiş ama hala ödenmemiş (aged receivable)
  (() => {
    const lines = [
      line('ln-011-1', 'Eğitim seansı (4 oturum)', 4, 50000, 0, 0.2),
    ];
    const totals = sumLines(lines);
    return {
      id: 'inv-011',
      invoiceNumber: 'EA20260413000091',
      type: 'e_arsiv',
      status: 'issued',
      customer: CUSTOMERS[4],
      issuedAt: '2026-04-13T16:00:00+03:00',
      dueAt: '2026-04-20',
      ...totals,
      paidMinor: 0,
      balanceMinor: totals.totalMinor,
      linkedOrderId: 'order-011',
      linkedBookingId: 'bkg-9811',
      lines,
      payments: [],
      deliveryStatus: 'sent_email',
      pdfUrl: '/mock-invoices/inv-011.pdf',
      shareUrl: 'https://pay.petota.biz/inv/011',
      gibCode: 'G1H2I3J4-K5L6-7890-1234-567890ABCDEF',
      integrator: 'parasut',
      integratorError: null,
      notes: null,
      createdAt: '2026-04-13T15:58:00+03:00',
      updatedAt: '2026-04-13T16:02:00+03:00',
    } satisfies InvoiceRow;
  })(),

  // #12 — draft, henüz issue edilmedi, POS'tan gelmedi (manuel)
  (() => {
    const lines = [
      line('ln-012-1', 'Özel hizmet — fotoğraf çekimi', 1, 35000, 0, 0.2),
    ];
    const totals = sumLines(lines);
    return {
      id: 'inv-012',
      invoiceNumber: null,
      type: 'e_arsiv',
      status: 'draft',
      customer: CUSTOMERS[1],
      issuedAt: null,
      dueAt: null,
      ...totals,
      paidMinor: 0,
      balanceMinor: totals.totalMinor,
      linkedOrderId: null,
      linkedBookingId: null,
      lines,
      payments: [],
      deliveryStatus: 'not_sent',
      pdfUrl: null,
      shareUrl: null,
      gibCode: null,
      integrator: null,
      integratorError: null,
      notes: 'Partner hediye çekimi — müşteri kabul bekleniyor',
      createdAt: iso(16, 0),
      updatedAt: iso(16, 0),
    } satisfies InvoiceRow;
  })(),
];

export const SEED_BRANCH_ID = BRANCH_ID;
export const SEED_BRANCH_NAME = BRANCH_NAME;
